import React, { useState, useEffect } from 'react';
import {
	Box,
	Typography,
	Divider,
	CircularProgress,
	Select,
	MenuItem,
	Snackbar,
	Grid,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	InputLabel,
	FormControl,
	IconButton,
	Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RestClient from '../../../../api/axiosInterceptor';
import MuiAlert from '@mui/material/Alert';
import { useTheme } from '@mui/material/styles';
import { useComponentStyles } from '../../../../hooks/useComponentStyles';

const Orders = ({ searchTerm = '' }) => {
	const theme = useTheme();
	const styles = useComponentStyles();
	const [orders, setOrders] = useState([]);
	const [detailsOpen, setDetailsOpen] = useState(false);
	const [selectedOrder, setSelectedOrder] = useState(null);
	const [filteredOrders, setFilteredOrders] = useState([]);
	const [snackbar, setSnackbar] = useState({
		open: false,
		message: '',
		severity: 'info',
	});
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	// Fetch orders function
	const fetchOrders = async () => {
		try {
			setLoading(true);
			const response = await RestClient.get('/medication/orders/');

			// Filter out orders with zero total quantity
			const fetchedOrders = response.data.filter((order) => {
				const totalQuantity =
					order.items?.reduce(
						(total, item) => total + (item.quantity || 0),
						0,
					) || 0;
				return totalQuantity > 0;
			});

			setOrders(fetchedOrders);
			setFilteredOrders(fetchedOrders);
		} catch (err) {
			console.error('Error fetching orders:', err);
			setError('Failed to load orders. Please try again later.');
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchOrders();
	}, []);

	// Order details
	const handleOrderClick = (event, order) => {
		if (
			event.target.closest('select') ||
			event.target.closest('.MuiSelect-root') ||
			event.target.closest('.MuiMenuItem-root')
		) {
			return;
		}
		setSelectedOrder(order);
		setDetailsOpen(true);
	};

	// Close the details dialog
	const handleCloseDetails = () => {
		setDetailsOpen(false);
		setSelectedOrder(null);
	};

	// Filter orders based on search term - using useEffect
	useEffect(() => {
		if (!orders || orders.length === 0) {
			setFilteredOrders([]);
			return;
		}

		if (!searchTerm || searchTerm.trim() === '') {
			setFilteredOrders(orders);
			return;
		}

		const term = searchTerm.toLowerCase().trim();

		// Filter orders based on search term
		const filtered = orders.filter((order) => {
			// Search in the formatted ID
			const formattedId = order.public_id
				? order.public_id.substring(0, 8)
				: 'Unknown';

			if (formattedId.toLowerCase().includes(term)) {
				return true;
			}

			// Search in email/customer
			if ((order.email || '').toLowerCase().includes(term)) {
				return true;
			}

			// Search in status
			if ((order.status || '').toLowerCase().includes(term)) {
				return true;
			}

			// Search in payment method
			if ((order.payment_method || '').toLowerCase().includes(term)) {
				return true;
			}

			return false;
		});
		setFilteredOrders(filtered);
	}, [searchTerm, orders]);

	// Helper function to get status styling
	const getStatusStyle = (status) => {
		switch (status) {
			case 'Pending':
				return { bgcolor: '#FEF9C3', color: '#854D0E' };
			case 'Processing':
				return { bgcolor: '#E0F2FE', color: '#0369A1' };
			case 'Completed':
				return { bgcolor: '#DCFCE7', color: '#166534' };
			case 'Cancelled':
				return { bgcolor: '#FEE2E2', color: '#B91C1C' };
			default:
				return { bgcolor: '#F3F4F6', color: '#4B5563' };
		}
	};

	const updateOrderStatus = async (orderId, newStatus) => {
		try {
			setLoading(true);

			// Find the order first, before using it
			const order = orders.find(
				(o) => o.public_id === orderId || o.id === orderId.replace(/-/g, ''),
			);

			if (!order) {
				console.error(`Order with ID ${orderId} not found`);
				throw new Error('Order not found');
			}

			// Save previous status before updating
			const previousStatus = order.status;

			// Update local state for instant UI feedback
			setOrders((prevOrders) =>
				prevOrders.map((o) => {
					if (o.public_id === orderId || o.id === orderId.replace(/-/g, '')) {
						return { ...o, status: newStatus };
					}
					return o;
				}),
			);

			// Standard status update
			const updateUrl = `/medication/orders/${orderId}/update_status/`;
			await RestClient.patch(updateUrl, {
				status: newStatus,
			});

			// Handle inventory updates based on status change
			if (order.items && Array.isArray(order.items)) {
				// Case 1: Completing an order - reduce stock
				if (newStatus === 'Completed') {
					console.log('DEBUG: Processing completed order, reducing stock');

					for (const item of order.items) {
						if (item.drug && item.drug.id && item.quantity) {
							console.log(
								`DEBUG: Reducing stock for drug ${item.drug.id} by ${item.quantity}`,
							);

							await RestClient.patch(
								`/medication/drug/${item.drug.id}/update_stock/`,
								{
									quantity_change: -item.quantity, // Negative to reduce stock
								},
							);
						}
					}

					// Send order confirmation - use the actual order ID
					try {
						console.log(
							'DEBUG: Sending order confirmation for order:',
							order.id,
						);

						const confirmationResponse = await RestClient.post(
							`/medication/orders/${order.id}/confirm/`,
						);

						console.log('DEBUG: Confirmation response:', confirmationResponse);

						// Show enhanced success message
						setSnackbar({
							open: true,
							message: `Order #${order.public_id} completed and confirmation sent to customer`,
							severity: 'success',
						});
					} catch (confirmErr) {
						console.error('Error sending confirmation:', confirmErr);

						// Still show regular success message if confirmation fails
						setSnackbar({
							open: true,
							message: `Order status updated to ${newStatus} (email notification failed)`,
							severity: 'warning',
						});
					}
				}
				// Case 2: Cancelling a completed order - restore stock
				else if (newStatus === 'Cancelled' && previousStatus === 'Completed') {
					console.log('DEBUG: Cancelling completed order, restoring stock');

					for (const item of order.items) {
						if (item.drug && item.drug.id && item.quantity) {
							console.log(
								`DEBUG: Restoring stock for drug ${item.drug.id} by ${item.quantity}`,
							);

							await RestClient.patch(
								`/medication/drug/${item.drug.id}/update_stock/`,
								{
									quantity_change: item.quantity, // Positive to increase stock
								},
							);
						}
					}

					// Show cancellation message
					setSnackbar({
						open: true,
						message: `Order #${order.public_id} cancelled and inventory restored`,
						severity: 'info',
					});
				} else {
					// Default success message for other status changes
					setSnackbar({
						open: true,
						message: `Order status updated to ${newStatus}`,
						severity: 'success',
					});
				}
			}

			// Refresh inventory on other tabs
			console.log('DEBUG: Dispatching inventoryUpdated event');
			window.dispatchEvent(new CustomEvent('inventoryUpdated'));
		} catch (error) {
			console.error('Error updating order status:', error);
			fetchOrders();
		} finally {
			setLoading(false);
		}
	};

	// Render loading state
	if (loading) {
		return (
			<Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
				<CircularProgress />
			</Box>
		);
	}

	// Render error state
	if (error) {
		return (
			<Box sx={{ color: 'error.main', textAlign: 'center', p: 4 }}>{error}</Box>
		);
	}

	const statLabelStyle = {
		fontSize: { xs: '0.8rem', sm: '0.85rem' },
		whiteSpace: 'nowrap',
		fontWeight: 'bold',
	};

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
			{/* Stats Summary */}
			<Box sx={styles.statsSummaryCard}>
				<Typography
					variant="subtitle1"
					sx={{
						...styles.title,
						mb: 2,
						fontWeight: 'bold',
					}}
				>
					Overall Orders Summary
				</Typography>

				<Box sx={styles.statsContainer}>
					{/* Total Orders */}
					<Box
						sx={{
							...styles.statsItem,
							...styles.statsItemBorderRight,
						}}
					>
						<Typography variant="body2" sx={styles.statsLabel}>
							Total Orders
						</Typography>
						<Typography variant="h6" sx={styles.statsValue}>
							{orders.length}
						</Typography>
					</Box>

					{/* Confirmed */}
					<Box
						sx={{
							...styles.statsItem,
							borderRight: {
								xs: 'none',
								sm: `1px solid ${
									theme.palette.mode === 'dark'
										? theme.palette.customColors?.cardBorder ||
											'rgba(255, 255, 255, 0.1)'
										: 'rgba(93, 102, 121, 0.3)'
								}`,
							},
						}}
					>
						<Typography variant="body2" sx={styles.statsLabel}>
							Confirmed Orders
						</Typography>
						<Box sx={styles.revenueDetailsContainer}>
							<Typography variant="h6" sx={styles.statsValue}>
								{orders.filter((order) => order.status === 'Completed').length}
							</Typography>
							<Box sx={styles.revenueSubstatContainer}>
								<Typography variant="caption" sx={styles.revenueLabel}>
									Revenue
								</Typography>
								<Typography variant="body2" sx={styles.revenueValue}>
									{orders
										.filter((order) => order.status === 'Completed')
										.reduce(
											(sum, order) =>
												sum + (parseFloat(order.total_price) || 0),
											0,
										)
										.toFixed(0)}
								</Typography>
							</Box>
						</Box>
					</Box>

					{/* Pending */}
					<Box
						sx={{
							...styles.statsItem,
							...styles.statsItemBorderRight,
							...styles.statsItemSecondRow,
						}}
					>
						<Typography variant="body2" sx={styles.statsLabel}>
							Pending Orders
						</Typography>
						<Typography
							variant="h6"
							sx={{
								...styles.statsValue,
								...styles.statsValueWarning,
							}}
						>
							{orders.filter((order) => order.status === 'Pending').length}
						</Typography>
					</Box>

					{/* Cancelled */}
					<Box
						sx={{
							...styles.statsItem,
							...styles.statsItemSecondRow,
						}}
					>
						<Typography variant="body2" sx={styles.statsLabel}>
							Cancelled Orders
						</Typography>
						<Typography
							variant="h6"
							sx={{
								...styles.statsValue,
								...styles.statsValueDanger,
							}}
						>
							{orders.filter((order) => order.status === 'Cancelled').length}
						</Typography>
					</Box>
				</Box>
			</Box>

			{/* Orders Table/Cards */}
			<Box sx={styles.ordersCard}>
				<Box sx={styles.ordersHeader}>
					<Typography variant="subtitle1" sx={styles.ordersTitle}>
						Orders
					</Typography>
				</Box>

				{/* Desktop Table Header - Hidden on small screens */}
				<Box sx={styles.ordersTableHeader}>
					<Box sx={{ width: '40px', textAlign: 'center' }}>S/N</Box>
					<Box sx={styles.ordersTableHeaderCell}>Order ID</Box>
					<Box sx={styles.ordersTableHeaderCell}>Quantity</Box>
					<Box sx={styles.ordersTableHeaderCell}>Order Value</Box>
					<Box sx={styles.ordersTableHeaderCell}>Customer Name</Box>
					<Box sx={styles.ordersTableHeaderCell}>Destination</Box>
					<Box sx={styles.ordersTableHeaderCell}>Payment</Box>
					<Box sx={styles.ordersTableHeaderCell}>Created At</Box>
					<Box sx={styles.ordersTableHeaderCell}>Status</Box>
				</Box>

				{/* Table/Card body */}
				<Box sx={styles.ordersTableBody}>
					{filteredOrders.length === 0 ? (
						<Box sx={styles.ordersEmptyState}>
							{searchTerm
								? `No orders found matching "${searchTerm}"`
								: 'No orders found'}
						</Box>
					) : (
						filteredOrders.map((order, index) => {
							// Calculate total quantity from items
							const totalQuantity = order.items.reduce(
								(total, item) => total + item.quantity,
								0,
							);
							const formattedOrderId = order.public_id
								? order.public_id.substring(0, 8)
								: 'Unknown';
							const orderDate = new Date(
								order.created_at || Date.now(),
							).toLocaleDateString('en-US', {
								month: 'short',
								day: 'numeric',
								year: 'numeric',
							});

							const orderNumber = index + 1;

							return (
								<React.Fragment key={order.id}>
									{/* Desktop View - Table Row */}
									<Box
										sx={styles.orderRowDesktop}
										onClick={(event) => handleOrderClick(event, order)}
									>
										<Box sx={styles.orderSerialContainer}>
											<Typography variant="body2">{orderNumber}</Typography>
										</Box>
										<Box sx={styles.orderIdCell}>{formattedOrderId}</Box>
										<Box sx={styles.orderCell}>{totalQuantity}</Box>
										<Box sx={styles.orderCell}>
											{order.total_price
												? `KES ${parseFloat(order.total_price).toFixed(2)}`
												: 'N/A'}
										</Box>
										<Box sx={styles.orderCell}>
											{order.email ? order.email.split('@')[0] : 'Guest'}
										</Box>
										<Box sx={styles.orderCell}>
											{order.shipping_address || 'Store Pickup'}
										</Box>
										<Box sx={styles.orderCell}>
											{order.payment_method ||
												(order.payment_status === 'Failed' ? (
													<span style={styles.paymentFailedText}>Failed</span>
												) : (
													<span style={styles.paymentPendingText}>Pending</span>
												))}
										</Box>
										<Box sx={styles.orderCell}>{orderDate}</Box>
										<Box sx={styles.orderStatusContainer}>
											<span style={styles.orderStatusBadge(order.status)}>
												{order.status}
											</span>

											<Select
												size="small"
												value={order.status || ''}
												displayEmpty
												sx={styles.orderStatusDropdown}
												onChange={(e) => {
													if (e.target.value) {
														updateOrderStatus(order.public_id, e.target.value);
													}
												}}
											>
												<MenuItem value="" disabled>
													<em>Change</em>
												</MenuItem>
												<MenuItem value="Pending">Pending</MenuItem>
												<MenuItem value="Processing">Processing</MenuItem>
												<MenuItem value="Completed">Completed</MenuItem>
												<MenuItem value="Cancelled">Cancelled</MenuItem>
											</Select>
										</Box>
									</Box>

									{/* Mobile View - Card */}
									<Box
										sx={styles.orderRowMobile}
										onClick={(event) => handleOrderClick(event, order)}
									>
										<Box sx={styles.orderSerialBadge}>{index + 1}</Box>

										{/* Header with ID and Status */}
										<Box sx={styles.orderMobileHeader}>
											<Typography sx={{ fontWeight: 'bold' }}>
												#{formattedOrderId}
											</Typography>
											<Box>
												<span style={styles.orderStatusBadge(order.status)}>
													{order.status}
												</span>
											</Box>
										</Box>

										{/* Order details */}
										<Grid container spacing={1} sx={{ mb: 1.5 }}>
											<Grid item xs={6}>
												<Typography
													variant="caption"
													sx={styles.orderMobileLabel}
												>
													Customer
												</Typography>
												<Typography variant="body2">
													{order.email ? order.email.split('@')[0] : 'Guest'}
												</Typography>
											</Grid>
											<Grid item xs={6}>
												<Typography
													variant="caption"
													sx={styles.orderMobileLabel}
												>
													Date
												</Typography>
												<Typography variant="body2">{orderDate}</Typography>
											</Grid>
											<Grid item xs={6}>
												<Typography
													variant="caption"
													sx={styles.orderMobileLabel}
												>
													Items
												</Typography>
												<Typography variant="body2">{totalQuantity}</Typography>
											</Grid>
											<Grid item xs={6}>
												<Typography
													variant="caption"
													sx={styles.orderMobileLabel}
												>
													Total
												</Typography>
												<Typography variant="body2" sx={{ fontWeight: '500' }}>
													{order.total_price
														? `KES ${parseFloat(order.total_price).toFixed(2)}`
														: 'N/A'}
												</Typography>
											</Grid>
											<Grid item xs={6}>
												<Typography
													variant="caption"
													sx={styles.orderMobileLabel}
												>
													Destination
												</Typography>
												<Typography variant="body2">
													{order.shipping_address || 'Store Pickup'}
												</Typography>
											</Grid>
											<Grid item xs={6}>
												<Typography
													variant="caption"
													sx={styles.orderMobileLabel}
												>
													Payment
												</Typography>
												<Typography variant="body2">
													{order.payment_method ||
														(order.payment_status === 'Failed' ? (
															<span style={styles.paymentFailedText}>
																Failed
															</span>
														) : (
															<span style={styles.paymentPendingText}>
																Pending
															</span>
														))}
												</Typography>
											</Grid>
										</Grid>

										{/* Actions */}
										<Box sx={{ mt: 1 }}>
											<Select
												size="small"
												fullWidth
												value=""
												displayEmpty
												sx={styles.orderMobileDropdown}
												onChange={(e) => {
													if (e.target.value) {
														updateOrderStatus(order.public_id, e.target.value);
													}
												}}
											>
												<MenuItem value="" disabled>
													<em>Update Status...</em>
												</MenuItem>
												<MenuItem value="Pending">Pending</MenuItem>
												<MenuItem value="Processing">Processing</MenuItem>
												<MenuItem value="Completed">Completed</MenuItem>
												<MenuItem value="Cancelled">Cancelled</MenuItem>
											</Select>
										</Box>
									</Box>
								</React.Fragment>
							);
						})
					)}
				</Box>
			</Box>
			{/* Alert message */}
			<Snackbar
				open={snackbar.open}
				autoHideDuration={6000}
				onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
			>
				<MuiAlert
					elevation={6}
					variant="filled"
					onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
					severity={snackbar.severity}
				>
					{snackbar.message}
				</MuiAlert>
			</Snackbar>
			{/* Order Details Dialog */}
			<Dialog
				open={detailsOpen}
				onClose={handleCloseDetails}
				maxWidth="md"
				fullWidth
				sx={{
					'& .MuiDialog-paper': {
						borderRadius: 2,
						boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
					},
				}}
			>
				{selectedOrder && (
					<>
						<DialogTitle sx={{ borderBottom: '1px solid #F3F4F6', pb: 2 }}>
							<Box
								sx={{
									display: 'flex',
									justifyContent: 'space-between',
									alignItems: 'center',
								}}
							>
								<Typography variant="h6">
									Order #
									{selectedOrder.public_id?.slice(-6).toUpperCase() ||
										selectedOrder.id}
								</Typography>
								<IconButton onClick={handleCloseDetails}>
									<CloseIcon />
								</IconButton>
							</Box>
						</DialogTitle>
						<DialogContent sx={{ pt: 3 }}>
							<Grid container spacing={3}>
								{/* Order Information */}
								<Grid item xs={12} md={6}>
									<Typography
										variant="subtitle1"
										fontWeight="bold"
										gutterBottom
									>
										Order Information
									</Typography>
									<Box sx={{ mt: 1 }}>
										<Box sx={{ display: 'flex', mb: 1 }}>
											<Typography
												variant="body2"
												sx={{ fontWeight: 'medium', width: '130px' }}
											>
												Order Date:
											</Typography>
											<Typography variant="body2">
												{new Date(selectedOrder.created_at).toLocaleString()}
											</Typography>
										</Box>
										<Box sx={{ display: 'flex', mb: 1 }}>
											<Typography
												variant="body2"
												sx={{ fontWeight: 'medium', width: '130px' }}
											>
												Total Amount:
											</Typography>
											<Typography variant="body2">
												{selectedOrder.total_price
													? `KES ${parseFloat(selectedOrder.total_price).toFixed(2)}`
													: 'N/A'}
											</Typography>
										</Box>
										<Box sx={{ display: 'flex', mb: 1 }}>
											<Typography
												variant="body2"
												sx={{ fontWeight: 'medium', width: '130px' }}
											>
												Status:
											</Typography>
											<Typography
												variant="body2"
												sx={{
													display: 'inline-block',
													padding: '4px 8px',
													borderRadius: '12px',
													fontSize: '0.75rem',
													backgroundColor: getStatusStyle(selectedOrder.status)
														.bgcolor,
													color: getStatusStyle(selectedOrder.status).color,
												}}
											>
												{selectedOrder.status}
											</Typography>
										</Box>
										<Box sx={{ display: 'flex', mb: 1 }}>
											<Typography
												variant="body2"
												sx={{ fontWeight: 'medium', width: '130px' }}
											>
												Payment Method:
											</Typography>
											<Typography variant="body2">
												{selectedOrder.payment_method ||
													(selectedOrder.payment_status === 'Failed'
														? 'Failed'
														: 'Pending')}
											</Typography>
										</Box>
									</Box>
								</Grid>

								{/* Customer Information */}
								<Grid item xs={12} md={6}>
									<Typography
										variant="subtitle1"
										fontWeight="bold"
										gutterBottom
									>
										Customer Information
									</Typography>
									<Box sx={{ mt: 1 }}>
										<Box sx={{ display: 'flex', mb: 1 }}>
											<Typography
												variant="body2"
												sx={{ fontWeight: 'medium', width: '130px' }}
											>
												Customer:
											</Typography>
											<Typography variant="body2">
												{selectedOrder.email || 'Guest Customer'}
											</Typography>
										</Box>
										<Box sx={{ display: 'flex', mb: 1 }}>
											<Typography
												variant="body2"
												sx={{ fontWeight: 'medium', width: '130px' }}
											>
												Shipping Address:
											</Typography>
											<Typography variant="body2">
												{selectedOrder.shipping_address || 'Store Pickup'}
											</Typography>
										</Box>
										<Box sx={{ display: 'flex', mb: 1 }}>
											<Typography
												variant="body2"
												sx={{ fontWeight: 'medium', width: '130px' }}
											>
												Phone Number:
											</Typography>
											<Typography variant="body2">
												{selectedOrder.phone_number || 'N/A'}
											</Typography>
										</Box>
									</Box>
								</Grid>

								{/* Order Items */}
								<Grid item xs={12}>
									<Typography
										variant="subtitle1"
										fontWeight="bold"
										gutterBottom
									>
										Order Items
									</Typography>
									<TableContainer
										sx={{ mt: 1, border: '1px solid #F3F4F6', borderRadius: 1 }}
									>
										<Table size="small">
											<TableHead>
												<TableRow sx={{ bgcolor: '#F9FAFB' }}>
													<TableCell>Product</TableCell>
													<TableCell align="center">Quantity</TableCell>
													<TableCell align="right">Unit Price</TableCell>
													<TableCell align="right">Total</TableCell>
												</TableRow>
											</TableHead>
											<TableBody>
												{selectedOrder.order_items?.map((item, index) => (
													<TableRow key={index}>
														<TableCell>{item.drug_name || item.name}</TableCell>
														<TableCell align="center">
															{item.quantity}
														</TableCell>
														<TableCell align="right">
															KES{' '}
															{parseFloat(
																item.price || item.unit_price,
															).toFixed(2)}
														</TableCell>
														<TableCell align="right">
															KES{' '}
															{parseFloat(
																(item.price || item.unit_price) * item.quantity,
															).toFixed(2)}
														</TableCell>
													</TableRow>
												))}
											</TableBody>
										</Table>
									</TableContainer>
								</Grid>
							</Grid>
						</DialogContent>
						<DialogActions sx={{ borderTop: '1px solid #F3F4F6', p: 2 }}>
							<Button
								variant="contained"
								color="primary"
								onClick={handleCloseDetails}
							>
								Close
							</Button>
						</DialogActions>
					</>
				)}
			</Dialog>
		</Box>
	);
};

export default Orders;
